import React, { useState } from "react";
import { Drawer, Box, Grid, TextField, Typography, IconButton, Button as MuiButton, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { validationTipoCirurgiaSchema } from "../../../schemas/validationSchema.ts";
import { useFeedback } from "../../../contexts/FeedbackContext.tsx";
import tipoCirurgiaService from "../../../services/tipoCirurgiaService.ts";
import { TipoCirurgia } from "../../../models/TipoCirurgia.ts";

interface CreateTipoCirurgiaDrawerProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (r: any) => void;
}

// Mesma receita de cores dos demais módulos — sem ThemeProvider do MUI,
// estados de label/borda caem no tema claro padrão do MUI, sem contraste
// no fundo escuro. CSS vars do design system em vez de tokens de tema.
const fieldSx = {
  "& .MuiInputLabel-root": { color: "var(--ink-faint)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--blue)" },
  "& .MuiOutlinedInput-root": {
    color: "var(--ink)",
    "& fieldset": { borderColor: "var(--border-strong)" },
    "&:hover fieldset": { borderColor: "var(--border-strong)" },
    "&.Mui-focused fieldset": { borderColor: "var(--blue)" },
  },
  "& .MuiFormHelperText-root": { color: "var(--red)" },
};

const scrollSx = {
  "&::-webkit-scrollbar": { width: 8 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": { background: "var(--border-strong)", borderRadius: 4 },
};

const CreateTipoCirurgiaDrawer: React.FC<CreateTipoCirurgiaDrawerProps> = ({ open, onClose, onCreated }) => {
  const { showFeedback } = useFeedback();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(validationTipoCirurgiaSchema),
  });

  const handleClose = () => {
    reset({});
    onClose();
  };

  const onSubmit = async (data: any) => {
    // id vai 0 aqui só porque o construtor exige um valor — o service de
    // criação remove o campo id do payload antes de enviar (ver tipoCirurgiaService.create).
    const novoTipo = new TipoCirurgia(0, data.nome, data.observacao);

    try {
      setIsLoading(true);
      const resp = await tipoCirurgiaService.create(novoTipo);
      showFeedback(resp.message, "success");
      onCreated?.(resp);
      reset({});
    } catch (err: any) {
      showFeedback(err.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 460 },
          bgcolor: "var(--surface)",
          color: "var(--ink)",
          borderLeft: "1px solid var(--border)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Cabeçalho fixo */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", px: 3, py: 2.5, borderBottom: "1px solid var(--border)", flex: "0 0 auto" }}>
        <div>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Novo Tipo de Cirurgia</Typography>
          <Typography variant="body2" sx={{ color: "var(--ink-faint)", mt: 0.25 }}>Cadastre um novo tipo de cirurgia</Typography>
        </div>
        <IconButton onClick={handleClose} size="small" sx={{ color: "var(--ink-faint)" }} aria-label="Fechar">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Corpo com scroll — flex:1 + overflowY:auto, o resto do Drawer fica fixo */}
      <Box
        component="form"
        id="create-tipocirurgia-form"
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ flex: 1, overflowY: "auto", px: 3, py: 3, ...scrollSx }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Nome do Tipo de Cirurgia*" size="small" fullWidth sx={fieldSx}
              {...register("nome")} autoComplete="off" error={!!errors.nome} helperText={errors.nome?.message as string}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Observação" size="small" fullWidth multiline minRows={3} sx={fieldSx}
              {...register("observacao")} error={!!errors.observacao} helperText={errors.observacao?.message as string}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Rodapé fixo */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, px: 3, py: 2.5, borderTop: "1px solid var(--border)", flex: "0 0 auto" }}>
        <MuiButton onClick={handleClose} disabled={isLoading} sx={{ color: "var(--ink-soft)" }}>
          Cancelar
        </MuiButton>
        <MuiButton type="submit" form="create-tipocirurgia-form" variant="contained" disabled={isLoading} disableElevation>
          {isLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Cadastrar"}
        </MuiButton>
      </Box>
    </Drawer>
  );
};

export default CreateTipoCirurgiaDrawer;
